import React from "react";
import { Box, Typography, Paper, Divider } from "@mui/material";
import { useDrag } from "react-dnd";
import {
  Warehouse as RackIcon,
  Category as ZoneIcon,
  SquareFoot as WallIcon,
  MeetingRoom as DoorIcon,
  StraightenOutlined as AisleIcon,
  ViewColumn as ColumnIcon,
} from "@mui/icons-material";

const DraggableElement = ({ type, icon: Icon, label, color, defaultWidth, defaultHeight }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type,
    item: { type, defaultWidth, defaultHeight, defaultColor: color },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [type, defaultWidth, defaultHeight, color]);

  return (
    <Paper
      ref={drag}
      sx={{
        p: 2,
        mb: 1.5,
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? "#f0f0f0" : "white",
        border: `2px solid ${color}`,
        borderRadius: 2,
        transition: "all 0.2s",
        userSelect: "none",
        touchAction: "none",
        "&:hover": {
          transform: "translateX(4px)",
          boxShadow: 2,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} sx={{ pointerEvents: "none" }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${color}20`,
            borderRadius: 1,
          }}
        >
          <Icon sx={{ color, fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight="600">
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Auf Leinwand ziehen
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const Toolbar = () => {
  const elements = [
    {
      type: "RACK",
      icon: RackIcon,
      label: "Regal",
      color: "#4361ee",
      defaultWidth: 2,
      defaultHeight: 3,
    },
    {
      type: "ZONE",
      icon: ZoneIcon,
      label: "Zone",
      color: "#2ecc71",
      defaultWidth: 10,
      defaultHeight: 10,
    },
    {
      type: "WALL",
      icon: WallIcon,
      label: "Wand",
      color: "#95a5a6",
      defaultWidth: 1,
      defaultHeight: 10,
    },
    {
      type: "DOOR",
      icon: DoorIcon,
      label: "Tür",
      color: "#f39c12",
      defaultWidth: 1,
      defaultHeight: 2,
    },
    {
      type: "AISLE",
      icon: AisleIcon,
      label: "Gang",
      color: "#3498db",
      defaultWidth: 2,
      defaultHeight: 10,
    },
    {
      type: "COLUMN",
      icon: ColumnIcon,
      label: "Säule",
      color: "#7f8c8d",
      defaultWidth: 1,
      defaultHeight: 1,
    },
  ];

  return (
    <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
      <Typography variant="h6" fontWeight="600" mb={2}>
        Elemente
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Ziehen Sie Elemente auf die Leinwand, um Ihr Lagerlayout zu erstellen
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" fontWeight="600" mb={1.5} color="text.secondary">
        LAGERUNG
      </Typography>
      {elements.slice(0, 2).map((element) => (
        <DraggableElement key={element.type} {...element} />
      ))}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight="600" mb={1.5} color="text.secondary">
        FUNKTIONEN
      </Typography>
      {elements.slice(2).map((element) => (
        <DraggableElement key={element.type} {...element} />
      ))}

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="caption" display="block" mb={1} fontWeight="600">
          💡 Tipps
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          • Elemente auf Leinwand ziehen
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          • Klicken zum Auswählen
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          • Strg+Klick für Mehrfachauswahl
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          • Entf-Taste zum Entfernen
        </Typography>
      </Box>
    </Box>
  );
};

export default Toolbar;
